import type { ShiftPerformance, Trends } from './api'
import { dateText, translate, type Lang } from './i18n'

/**
 * El reporte del periodo, para imprimir o presentar (§16.3).
 *
 * INSIGHT exportaba CSV, que sirve para volver a analizar y no para llevar a una junta: no tiene
 * de qué agencia es, de qué periodo, ni con qué salvedades se leen esas cifras. Esto es lo otro.
 *
 * **Se imprime desde el navegador.** No hay biblioteca de PDF ni de Excel detrás, y eso es
 * deliberado: el producto se instala en el servidor de una corporación policial, donde cada
 * paquete de terceros es superficie que hay que vigilar mientras el producto viva. El navegador ya
 * sabe imprimir y guardar como PDF.
 */
export function ReportView({
  lang, agency, trends, shifts, user, issued,
}: {
  lang: Lang
  agency: string
  trends: Trends
  shifts: ShiftPerformance[]
  user: string
  /**
   * El día en que se emite, como <c>AAAA-MM-DD</c>.
   *
   * Entra como dato en vez de leerse del reloj aquí dentro. Así la prueba puede afirmar la fecha
   * que sale impresa en vez de conformarse con un comodín, que es justo lo que dejó pasar un
   * «Invalid Date» hasta que se vio en pantalla.
   */
  issued: string
}) {
  const parciales = shifts.filter((s) => s.isPartial).length
  const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`)

  return (
    <article className="report">
      <header>
        <h1>{translate(lang, 'report.title')}</h1>
        <p className="report-agency">{agency}</p>
        <p className="meta">
          {translate(lang, 'trends.period', [dateText(lang, trends.from), dateText(lang, trends.to), trends.totals.shifts])}
        </p>
        {/* Quién y cuándo lo sacó. Un reporte que circula sin eso no se puede volver a pedir
            igual, y nadie sabe si está mirando el de la semana pasada. */}
        <p className="meta">{translate(lang, 'report.issued', [dateText(lang, issued), user])}</p>
      </header>

      {/* Antes de las cifras y no al pie. Un lector que llega a la última página ya se formó una
          opinión; la salvedad tiene que estar donde todavía sirve. */}
      <p className="report-caveat">{translate(lang, 'insight.caveat')}</p>

      {parciales > 0 && (
        // Promediar turnos parciales junto a los completos sin decirlo es la forma más fácil de
        // que un reporte mienta sin que nadie haya mentido.
        <p className="report-caveat">{translate(lang, 'report.partials', [parciales, shifts.length])}</p>
      )}

      <section>
        <h2>{translate(lang, 'trends.compliance')}</h2>
        <table className="compare">
          <tbody>
            <tr>
              <td>{translate(lang, 'trends.onTime')}</td>
              <td className="num">{pct(trends.totals.onTimeRate)}</td>
              <td className="meta">{trends.previous ? pct(trends.previous.onTimeRate) : '—'}</td>
            </tr>
            <tr>
              <td>{translate(lang, 'trends.presence')}</td>
              <td className="num">{pct(trends.totals.presenceRate)}</td>
              <td className="meta">{trends.previous ? pct(trends.previous.presenceRate) : '—'}</td>
            </tr>
          </tbody>
          <caption>
            {trends.previous
              ? translate(lang, 'trends.against', [trends.previous.shifts])
              : translate(lang, 'trends.noPrevious')}
          </caption>
        </table>
      </section>

      <section>
        <h2>{translate(lang, 'trends.work')}</h2>
        <table className="compare">
          <tbody>
            <tr><td>{translate(lang, 'report.planned')}</td><td className="num">{trends.totals.planned}</td></tr>
            <tr><td>{translate(lang, 'report.released')}</td><td className="num">{trends.totals.released}</td></tr>
            <tr><td>{translate(lang, 'report.completed')}</td><td className="num">{trends.totals.completed}</td></tr>
            <tr><td>{translate(lang, 'report.deferred')}</td><td className="num">{trends.totals.deferred}</td></tr>
            <tr><td>{translate(lang, 'report.incidents')}</td><td className="num">{trends.totals.incidents}</td></tr>
          </tbody>
        </table>
      </section>

      {trends.deviations.length > 0 && (
        <section>
          <h2>{translate(lang, 'trends.kinds')}</h2>
          <table className="compare">
            <tbody>
              {trends.deviations.map((d) => (
                <tr key={d.kind}>
                  <td>{translate(lang, `kind.${d.kind}`)}</td>
                  <td className="num">{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {trends.byDivision.length > 0 && (
        <section>
          <h2>{translate(lang, 'trends.byDivision')}</h2>
          <table className="compare">
            <thead>
              <tr>
                <th>{translate(lang, 'report.division')}</th>
                <th>{translate(lang, 'trends.onTime')}</th>
                <th>{translate(lang, 'trends.presence')}</th>
              </tr>
            </thead>
            <tbody>
              {trends.byDivision.map((d) => (
                <tr key={d.division}>
                  <td>{d.division}</td>
                  <td className="num">{pct(d.onTimeRate)}</td>
                  <td className="num">{pct(d.presenceRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <footer className="meta">{translate(lang, 'report.footer')}</footer>
    </article>
  )
}
